import { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import useUser from 'hooks/useUser';
import FullLoader from 'components/Loader/FullLoader';
import {
  ADMIN_INITIAL_POST_SETUP_PATH,
  ADMIN_INITIAL_SETUP_PATH,
} from 'pages/Onboarding/constants/constants';
import { isTenantOnboardingComplete } from 'utils/isTenantOnboardingComplete';
import { isTenantOnboardingSkipped } from 'utils/module/Onboarding/tenant';

interface OnboardingPendingGuardProps {
  children: ReactNode;
}

/**
 * Gates `/onboarding-pending` — the "waiting room" shown to non-super users when their
 * tenant hasn't been onboarded yet. Reads `is_onboarding_completed` and
 * `is_onboarding_skipped` from `useUser().currentTenant`.
 *
 *   completed | skipped | user        → result
 *   true      | -       | anyone      → /            (tenant is ready, no reason to wait)
 *   -         | true    | anyone      → /            (super-user skipped, app is open)
 *   false     | false   | super-user  → /onboarding-setup  (they should run the wizard)
 *   false     | false   | non-super   → render pending page
 */
export const OnboardingPendingGuard: FC<OnboardingPendingGuardProps> = ({
  children,
}) => {
  const { user, currentTenant } = useUser();

  // Wait for `useAuth` to populate user + tenant before deciding.
  if (!user?.id || !currentTenant) {
    return <FullLoader />;
  }

  const isCompleted = isTenantOnboardingComplete(currentTenant);
  const isSuperUser = Boolean(user.super_user);

  // Tenant already onboarded → no reason to wait, go to the app.
  if (isCompleted) {
    return <Navigate to={ADMIN_INITIAL_POST_SETUP_PATH} replace />;
  }

  // Super-user skipped → main app is open for everyone on this tenant.
  if (isTenantOnboardingSkipped(currentTenant)) {
    return <Navigate to={ADMIN_INITIAL_POST_SETUP_PATH} replace />;
  }

  // Super-users belong in the wizard, not the waiting room.
  if (isSuperUser) {
    return <Navigate to={ADMIN_INITIAL_SETUP_PATH} replace />;
  }

  return <>{children}</>;
};
